import { toast } from 'react-toastify';
import {
    AnalyticsDateParams,
    DashboardAnalyticsResponse,
    UserAnalyticsResponse,
    ProductAnalyticsResponse,
    OrderAnalyticsResponse,
    AuditAnalyticsResponse,
    ProductLifecycleAnalyticsResponse,
} from '@core/models/analytics.model';
import { IErrorModel } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import AnalyticsService from 'services/api/analytics.service';
import 'react-toastify/dist/ReactToastify.css';
//----------------------------------------------------------------------------------------------------------
interface IAnalyticsState {
    dashboardAnalytics: DashboardAnalyticsResponse | null;
    userAnalytics: UserAnalyticsResponse | null;
    productAnalytics: ProductAnalyticsResponse | null;
    orderAnalytics: OrderAnalyticsResponse | null;
    auditAnalytics: AuditAnalyticsResponse | null;
    productLifecycleAnalytics: ProductLifecycleAnalyticsResponse | null;
    error: IErrorModel | undefined;
    loading: boolean;
    dashboardLoading: boolean;
    userLoading: boolean;
    productLoading: boolean;
    orderLoading: boolean;
    auditLoading: boolean;
    productLifecycleLoading: boolean;
}

const initialState: IAnalyticsState = {
    dashboardAnalytics: null,
    userAnalytics: null,
    productAnalytics: null,
    orderAnalytics: null,
    auditAnalytics: null,
    productLifecycleAnalytics: null,
    error: {} as IErrorModel,
    loading: false,
    dashboardLoading: false,
    userLoading: false,
    productLoading: false,
    orderLoading: false,
    auditLoading: false,
    productLifecycleLoading: false,
};
//----------------------------------------------------------------------------------------------------------
export const getDashboardAnalyticsThunk = createAsyncThunk<DashboardAnalyticsResponse, AnalyticsDateParams | undefined, { rejectValue: IErrorModel }>(
    `/analytics/dashboard`,
    async (params, { rejectWithValue }: any) => {
        try {
            const response: any = await AnalyticsService.getDashboardAnalytics(params);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage, { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const getUserAnalyticsThunk = createAsyncThunk<UserAnalyticsResponse, AnalyticsDateParams | undefined, { rejectValue: IErrorModel }>(
    `/analytics/users`,
    async (params, { rejectWithValue }: any) => {
        try {
            const response: any = await AnalyticsService.getUserAnalytics(params);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage, { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const getProductAnalyticsThunk = createAsyncThunk<ProductAnalyticsResponse, void, { rejectValue: IErrorModel }>(
    `/analytics/products`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await AnalyticsService.getProductAnalytics();
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage, { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const getOrderAnalyticsThunk = createAsyncThunk<OrderAnalyticsResponse, AnalyticsDateParams | undefined, { rejectValue: IErrorModel }>(
    `/analytics/orders`,
    async (params, { rejectWithValue }: any) => {
        try {
            const response: any = await AnalyticsService.getOrderAnalytics(params);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage, { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const getAuditAnalyticsThunk = createAsyncThunk<AuditAnalyticsResponse, AnalyticsDateParams | undefined, { rejectValue: IErrorModel }>(
    `/analytics/audit`,
    async (params, { rejectWithValue }: any) => {
        try {
            const response: any = await AnalyticsService.getAuditAnalytics(params);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage, { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
export const getProductLifecycleAnalyticsThunk = createAsyncThunk<ProductLifecycleAnalyticsResponse, AnalyticsDateParams | undefined, { rejectValue: IErrorModel }>(
    `/analytics/product-lifecycle`,
    async (params, { rejectWithValue }: any) => {
        try {
            const response: any = await AnalyticsService.getProductLifecycleAnalytics(params);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage, { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);
//----------------------------------------------------------------------------------------------------------
const analyticsSlice = createSlice({
    name: `analytics`,
    initialState,
    extraReducers: (builder) => {
        // DASHBOARD ANALYTICS
        builder.addCase(getDashboardAnalyticsThunk.pending, (state: any, action: any) => {
            state.dashboardLoading = true;
            state.loading = true;
        });
        builder.addCase(getDashboardAnalyticsThunk.fulfilled, (state, action: any) => {
            state.dashboardLoading = false;
            state.loading = false;
            state.dashboardAnalytics = action.payload;
        });
        builder.addCase(getDashboardAnalyticsThunk.rejected, (state, action) => {
            state.dashboardLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // USER ANALYTICS
        builder.addCase(getUserAnalyticsThunk.pending, (state: any, action: any) => {
            state.userLoading = true;
            state.loading = true;
        });
        builder.addCase(getUserAnalyticsThunk.fulfilled, (state, action: any) => {
            state.userLoading = false;
            state.loading = false;
            state.userAnalytics = action.payload;
        });
        builder.addCase(getUserAnalyticsThunk.rejected, (state, action) => {
            state.userLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // PRODUCT ANALYTICS
        builder.addCase(getProductAnalyticsThunk.pending, (state: any, action: any) => {
            state.productLoading = true;
            state.loading = true;
        });
        builder.addCase(getProductAnalyticsThunk.fulfilled, (state, action: any) => {
            state.productLoading = false;
            state.loading = false;
            state.productAnalytics = action.payload;
        });
        builder.addCase(getProductAnalyticsThunk.rejected, (state, action) => {
            state.productLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // ORDER ANALYTICS
        builder.addCase(getOrderAnalyticsThunk.pending, (state: any, action: any) => {
            state.orderLoading = true;
            state.loading = true;
        });
        builder.addCase(getOrderAnalyticsThunk.fulfilled, (state, action: any) => {
            state.orderLoading = false;
            state.loading = false;
            state.orderAnalytics = action.payload;
        });
        builder.addCase(getOrderAnalyticsThunk.rejected, (state, action) => {
            state.orderLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // AUDIT ANALYTICS
        builder.addCase(getAuditAnalyticsThunk.pending, (state: any, action: any) => {
            state.auditLoading = true;
            state.loading = true;
        });
        builder.addCase(getAuditAnalyticsThunk.fulfilled, (state, action: any) => {
            state.auditLoading = false;
            state.loading = false;
            state.auditAnalytics = action.payload;
        });
        builder.addCase(getAuditAnalyticsThunk.rejected, (state, action) => {
            state.auditLoading = false;
            state.loading = false;
            state.error = action.payload;
        });

        // PRODUCT LIFECYCLE ANALYTICS
        builder.addCase(getProductLifecycleAnalyticsThunk.pending, (state: any, action: any) => {
            state.productLifecycleLoading = true;
            state.loading = true;
        });
        builder.addCase(getProductLifecycleAnalyticsThunk.fulfilled, (state, action: any) => {
            state.productLifecycleLoading = false;
            state.loading = false;
            state.productLifecycleAnalytics = action.payload;
        });
        builder.addCase(getProductLifecycleAnalyticsThunk.rejected, (state, action) => {
            state.productLifecycleLoading = false;
            state.loading = false;
            state.error = action.payload;
        });
    },
    reducers: {},
});
//----------------------------------------------------------------------------------------------------------
export default analyticsSlice;
export const analyticsReducer = analyticsSlice.reducer;
//----------------------------------------------------------------------------------------------------------

